"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Pencil, Trash2, Plus, MoreVertical } from "lucide-react";
import ConfirmDialog from "@/components/admin/shared/confirm-dialog";
import { toast } from "sonner";
import { getCountryByCode } from "@/lib/constants/countries";
import { PartnerFormDialog } from "./partner-form-dialog";
import type { Partner, PartnerFormValues } from "./types";

interface PartnersPageClientProps {
  venueId: number;
  venueName: string;
  userRole?: string;
  /** May add, edit and remove partners; defaults to super admins only. */
  canEdit?: boolean;
  initialPartners: Partner[];
}

export default function PartnersPageClient({
  venueId,
  venueName,
  userRole,
  canEdit,
  initialPartners,
}: PartnersPageClientProps) {
  const isSuperAdmin = canEdit ?? userRole === "super_admin";

  const [partners, setPartners] = useState<Partner[]>(initialPartners);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Partner | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Partner | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const apiBase = `/api/staff/venues/${venueId}/partners`;

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (partner: Partner) => {
    setEditing(partner);
    setFormOpen(true);
  };

  const handleSubmit = async (values: PartnerFormValues) => {
    setSubmitting(true);
    try {
      const isEdit = !!editing;
      const url = isEdit ? `${apiBase}/${editing.id}` : apiBase;
      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        toast.error(json.error || "Failed to save partner");
        return;
      }
      const saved = json.data as Partner;
      if (isEdit) {
        setPartners((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
        toast.success("Partner updated");
      } else {
        setPartners((prev) =>
          [saved, ...prev].sort((a, b) =>
            a.company_name.localeCompare(b.company_name),
          ),
        );
        toast.success("Partner created");
      }
      setFormOpen(false);
    } catch (err) {
      console.error("[partners] save failed", err);
      toast.error("Failed to save partner");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${apiBase}/${deleteTarget.id}`, {
        method: "DELETE",
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        toast.error(json.error || "Failed to delete partner");
        return;
      }
      setPartners((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      toast.success("Partner deleted");
      setDeleteTarget(null);
    } catch (err) {
      console.error("[partners] delete failed", err);
      toast.error("Failed to delete partner");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Partners</h1>
            <p className="text-gray-600 mt-2">
              Manage partner agencies booking on behalf of end customers for{" "}
              {venueName}.
            </p>
          </div>
          {isSuperAdmin && (
            <Button
              onClick={openCreate}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add partner
            </Button>
          )}
        </div>

        {partners.length > 0 ? (
          <div className="bg-white rounded-lg border shadow overflow-hidden">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow className="border-b-2">
                  <TableHead className="text-gray-700 font-semibold px-6 py-4">
                    Partner
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold px-6 py-4">
                    Org. number
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold px-6 py-4">
                    Country
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold px-6 py-4 text-right">
                    Commission
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold px-6 py-4 text-right">
                    Payment term
                  </TableHead>
                  {isSuperAdmin && (
                    <TableHead className="text-right text-gray-700 font-semibold px-6 py-4">
                      Actions
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map((partner) => {
                  const country = getCountryByCode(partner.country);
                  return (
                    <TableRow key={partner.id} className="hover:bg-gray-50">
                      <TableCell className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {partner.company_name}
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-sm text-gray-700">
                        {partner.organization_number || (
                          <span className="text-gray-400 italic">—</span>
                        )}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {country ? (
                          <span className="text-sm">
                            {country.flag} {country.name}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">—</span>
                        )}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-right">
                        <span className="font-semibold text-gray-900">
                          {partner.commission_percentage}%
                        </span>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-right text-sm text-gray-700">
                        {partner.payment_terms_days ?? 20} days
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell className="text-right px-6 py-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => openEdit(partner)}
                              >
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(partner)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border shadow overflow-hidden py-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Plus className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No partners yet
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Add partner agencies so their invoice info auto-fills and
                commission is applied to show fees on every booking.
              </p>
              {isSuperAdmin && (
                <Button
                  onClick={openCreate}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add your first partner
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <PartnerFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        partner={editing}
        onSubmit={handleSubmit}
        submitting={submitting}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete partner"
        description="Bookings already linked to this partner will keep their commission settings, but the partner won't be selectable on new bookings."
        itemName={deleteTarget?.company_name}
        itemSubtext={
          deleteTarget
            ? `${deleteTarget.commission_percentage}% commission`
            : undefined
        }
        confirmText="Delete"
        confirmLoadingText="Deleting..."
        isLoading={deleting}
      />
    </div>
  );
}
